import { useEffect, useRef } from "react";

const EMAIL_PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[a-z]{2,}$/i;

/** Delay before a typed address becomes a chip, or null when incomplete. */
export const autoCommitDelay = (
  draft: string,
  mode: "email" | "phone",
): number | null => {
  const value = draft.trim();
  if (!value) return null;
  if (mode === "email") {
    // A short pause instead of committing on ".com": addresses such as
    // name@company.com.mx or name@studio.company keep typing.
    return EMAIL_PATTERN.test(value) ? 700 : null;
  }
  const digits = value.replace(/\D/g, "");
  const complete =
    digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
  return complete && /^[\d\s+\-().]+$/.test(value) ? 400 : null;
};

/**
 * Project rule for email/phone recipient fields: once the typed value is a
 * complete address or number, add it as a chip without Enter or a click.
 */
export const useRecipientAutoCommit = (
  draft: string,
  mode: "email" | "phone",
  commit: (value: string) => void,
  enabled = true,
) => {
  const commitRef = useRef(commit);
  commitRef.current = commit;
  useEffect(() => {
    if (!enabled) return;
    const delay = autoCommitDelay(draft, mode);
    if (delay == null) return;
    const handle = window.setTimeout(() => commitRef.current(draft), delay);
    return () => window.clearTimeout(handle);
  }, [draft, mode, enabled]);
};
